import React, { useState } from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';

const SmtpSettings = ({ generalSettings, setGeneralSettings, user }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [testingSmtp, setTestingSmtp] = useState(false);
  const [smtpTestResult, setSmtpTestResult] = useState(null);

  const testSmtpSettings = async () => {
    setTestingSmtp(true);
    setSmtpTestResult(null);
    
    try {
      const response = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        },
        body: JSON.stringify({
          testEmail: user.email || generalSettings.smtpFrom || generalSettings.smtpUser || 'admin@gineex.de',
          smtpSettings: {
            smtpHost: generalSettings.smtpHost,
            smtpPort: generalSettings.smtpPort,
            smtpUser: generalSettings.smtpUser,
            smtpPassword: generalSettings.smtpPassword,
            smtpFrom: generalSettings.smtpFrom,
            smtpSecure: generalSettings.smtpSecure
          }
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'SMTP-Test fehlgeschlagen');
      }

      setSmtpTestResult({
        success: true,
        message: t('admin.smtp.testSuccess', { email: result.email || 'unknown' })
      });
    } catch (error) {
      setSmtpTestResult({
        success: false,
        message: t('admin.smtp.testFailedMsg', { error: error.message })
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  const sendTestEmail = async () => {
    if (!generalSettings.emailNotificationsEnabled) {
      setSmtpTestResult({
        success: false,
        message: t('admin.smtp.notificationsDisabled')
      });
      return;
    }

    setTestingSmtp(true);
    setSmtpTestResult(null);
    
    try {
      const response = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.token}`
        }
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Test-E-Mail fehlgeschlagen');
      }

      setSmtpTestResult({
        success: true,
        message: t('admin.smtp.emailSuccess', { email: result.email })
      });
    } catch (error) {
      setSmtpTestResult({
        success: false,
        message: t('admin.smtp.emailFailedMsg', { error: error.message })
      });
    } finally {
      setTestingSmtp(false);
    }
  };

  return (
    <div className="relative group mb-6">
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20'
        : 'glass-light-card border border-slate-200'
      } ${isDarkMode ? 'backdrop-blur-2xl' : ''} rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
      <div className={`absolute inset-0 ${isDarkMode
        ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
        : ''
      } rounded-3xl`}></div>

      <div className={`absolute bottom-6 left-12 w-1 h-1 ${isDarkMode ? 'bg-white/30' : 'hidden'} rounded-full animate-ping`}></div>
      
      <div className="relative p-6">
        <h4 className={`text-lg font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'} mb-6`}>{t('admin.smtp.title')}</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.smtp.server')}</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <input
                type="text"
                value={generalSettings.smtpHost}
                onChange={(e) => setGeneralSettings({...generalSettings, smtpHost: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-slate-900 placeholder-slate-400'
                }`}
                placeholder="smtp.example.com"
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'} transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>Port</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <input
                type="number"
                value={generalSettings.smtpPort}
                onChange={(e) => setGeneralSettings({...generalSettings, smtpPort: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-slate-900 placeholder-slate-400'
                }`}
                placeholder="587"
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'} transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.smtp.username')}</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <input
                type="text"
                value={generalSettings.smtpUser}
                onChange={(e) => setGeneralSettings({...generalSettings, smtpUser: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-slate-900 placeholder-slate-400'
                }`}
                placeholder="smtp-benutzer@example.com"
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'} transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          <div>
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.smtp.password')}</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <input
                type="password"
                value={generalSettings.smtpPassword}
                onChange={(e) => setGeneralSettings({...generalSettings, smtpPassword: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-slate-900 placeholder-slate-400'
                }`}
                placeholder="SMTP-Passwort"
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'} transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className={`block text-sm font-semibold ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-700'} mb-3`}>{t('admin.smtp.senderEmail')}</label>
            <div className="relative group/input">
              <div className={`absolute inset-0 ${isDarkMode
                ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                : 'bg-white border border-slate-200'
              } rounded-2xl`}></div>
              <input
                type="email"
                value={generalSettings.smtpFrom}
                onChange={(e) => setGeneralSettings({...generalSettings, smtpFrom: e.target.value})}
                className={`relative w-full px-4 py-4 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? 'focus:ring-blue-400/50' : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode
                  ? 'text-white placeholder-white/60 focus:shadow-[0_0_20px_rgba(59,130,246,0.4)] hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  : 'text-slate-900 placeholder-slate-400'
                }`}
                placeholder="noreply@example.com"
              />
              <div className={`absolute inset-0 rounded-2xl border border-transparent ${isDarkMode ? 'group-focus-within/input:border-blue-400/50' : 'group-focus-within/input:border-brand/40'} transition-all duration-300 pointer-events-none`}></div>
            </div>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center group/checkbox cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={generalSettings.smtpSecure}
                  onChange={(e) => setGeneralSettings({...generalSettings, smtpSecure: e.target.checked})}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${generalSettings.smtpSecure ? (isDarkMode ? 'bg-blue-500/60 border-blue-400/60 backdrop-blur-xl' : 'bg-brand border-brand') : (isDarkMode ? 'bg-gray-800/30 border-white/30 backdrop-blur-xl' : 'bg-white border-slate-300')}`}>
                  {generalSettings.smtpSecure && (
                    <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <span className={`ml-3 text-sm ${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-700'}`}>{t('admin.smtp.tls')}</span>
            </label>
          </div>

          <div className="md:col-span-2">
            <label className="flex items-center group/checkbox cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={generalSettings.emailNotificationsEnabled}
                  onChange={(e) => setGeneralSettings({...generalSettings, emailNotificationsEnabled: e.target.checked})}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 transition-all duration-200 ${generalSettings.emailNotificationsEnabled ? (isDarkMode ? 'bg-blue-500/60 border-blue-400/60 backdrop-blur-xl' : 'bg-brand border-brand') : (isDarkMode ? 'bg-gray-800/30 border-white/30 backdrop-blur-xl' : 'bg-white border-slate-300')}`}>
                  {generalSettings.emailNotificationsEnabled && (
                    <svg className="w-3 h-3 text-white absolute top-0.5 left-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
              </div>
              <span className={`ml-3 text-sm ${isDarkMode ? 'text-white/80 drop-shadow-sm' : 'text-slate-700'}`}>{t('admin.smtp.notificationsEnabled')}</span>
            </label>
            <p className={`text-sm ${isDarkMode ? 'text-white/60 drop-shadow-sm' : 'text-slate-600'} mt-2 ml-8`}>
              {t('admin.smtp.notificationsHint')}
            </p>
          </div>

          <div className="md:col-span-2 flex flex-wrap items-center gap-4">
            <button
              onClick={testSmtpSettings}
              disabled={testingSmtp || !generalSettings.smtpHost || !generalSettings.smtpPort}
              className={`relative group/test overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-emerald-600 hover:bg-emerald-700 shadow-sm'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(16,185,129,0.7) 0%, rgba(34,197,94,0.7) 50%, rgba(16,185,129,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(16,185,129,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/test:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>
                               
              <div className="relative z-10 px-4 py-3 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/test:drop-shadow-xl">
                {testingSmtp ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/80 border-t-transparent mr-2"></div>
                    {t('admin.smtp.testing')}
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2 transition-transform duration-300 group-hover/test:scale-110" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {t('admin.smtp.testConnection')}
                  </>
                )}
              </div>
              
              <div className={`absolute inset-0 opacity-0 group-hover/test:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/test:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>

            <button
              onClick={sendTestEmail}
              disabled={testingSmtp || !generalSettings.smtpHost || !generalSettings.smtpFrom}
              className={`relative group/email overflow-hidden rounded-2xl transition-all duration-300 transform hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 ${isDarkMode ? '' : 'bg-brand hover:bg-[#1D4ED8] shadow-sm'}`}
              style={isDarkMode ? {
                background: 'linear-gradient(135deg, rgba(59,130,246,0.7) 0%, rgba(79,70,229,0.7) 50%, rgba(59,130,246,0.7) 100%)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255,255,255,0.3)',
                boxShadow: '0 8px 32px rgba(59,130,246,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
              } : {}}
            >
              <div
                className={`absolute inset-0 rounded-2xl transition-all duration-300 opacity-0 group-hover/email:opacity-100 ${isDarkMode ? '' : 'hidden'}`}
                style={isDarkMode ? {
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.15) 50%, rgba(255,255,255,0.08) 100%)'
                } : {}}
              ></div>
                                
              <div className="relative z-10 px-4 py-3 flex items-center font-medium text-white transition-all duration-300 drop-shadow-lg group-hover/email:drop-shadow-xl">
                {testingSmtp ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/80 border-t-transparent mr-2"></div>
                    {t('admin.smtp.testing')}
                  </>
                ) : (
                  <>
                    <span className="mr-2 transition-transform duration-300 group-hover/email:scale-110">📧</span>
                    {t('admin.smtp.testEmail')}
                  </>
                )}
              </div>
              
              <div className={`absolute inset-0 opacity-0 group-hover/email:opacity-100 transition-opacity duration-500 ${isDarkMode ? '' : 'hidden'}`}>
                <div
                  className="absolute inset-0 transform -skew-x-12 -translate-x-full group-hover/email:translate-x-full transition-transform duration-800"
                  style={isDarkMode ? {
                    background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.12) 50%, transparent 100%)'
                  } : {}}
                ></div>
              </div>
            </button>
          </div>

          {smtpTestResult && (
            <div className="md:col-span-2 relative group/result">
              <div className={`absolute inset-0 rounded-2xl border ${
                smtpTestResult.success
                  ? (isDarkMode ? 'bg-emerald-500/10 border-emerald-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.2)]' : 'bg-emerald-50 border-emerald-200 shadow-sm')
                  : (isDarkMode ? 'bg-red-500/10 border-red-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.2)]' : 'bg-red-50 border-red-200 shadow-sm')
              }`}></div>
              <div className={`absolute inset-0 rounded-2xl ${
                isDarkMode
                  ? (smtpTestResult.success ? 'bg-gradient-to-r from-emerald-400/10 to-green-400/10' : 'bg-gradient-to-r from-red-400/10 to-pink-400/10')
                  : ''
              }`}></div>
              <div className={`absolute top-0 left-0 w-full h-0.5 rounded-t-2xl ${
                smtpTestResult.success
                  ? (isDarkMode ? 'bg-gradient-to-r from-emerald-400/70 to-green-400/70' : 'bg-emerald-400')
                  : (isDarkMode ? 'bg-gradient-to-r from-red-400/70 to-pink-400/70' : 'bg-red-400')
              }`}></div>
              <div className="relative p-4">
                <p className={`text-sm font-medium ${
                  smtpTestResult.success
                    ? (isDarkMode ? 'text-emerald-200 drop-shadow-sm' : 'text-emerald-700')
                    : (isDarkMode ? 'text-red-200 drop-shadow-sm' : 'text-red-700')
                }`}>
                  {smtpTestResult.message}
                </p>
              </div>
            </div>
          )}
        </div>
        
        <div className="mt-6 relative group/info">
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-blue-500/10 border border-blue-300/30 backdrop-blur-xl shadow-[0_8px_32px_rgba(59,130,246,0.2)]'
            : 'bg-brand/5 border border-brand/20 shadow-sm'
          } rounded-2xl`}></div>
          <div className={`absolute inset-0 ${isDarkMode
            ? 'bg-gradient-to-r from-blue-400/10 to-indigo-400/10'
            : ''
          } rounded-2xl`}></div>
          <div className="relative p-4">
            <p className={`${isDarkMode ? 'text-blue-200 drop-shadow-sm' : 'text-brand'} text-sm`}>
              {t('admin.smtp.hint')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmtpSettings;